"use client";

import { IMarketOutcomeFeedResponse } from "@finbackoffice/clientbff-client";
import {
    FC,
    memo,
    ReactNode,
    SyntheticEvent,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import { usePrevious } from "@finbackoffice/site-core";
import { ConfigContextV2 } from "contexts";
import { OddFormatter, getBetStatus } from "@finbackoffice/fe-core";
import { MarketStatus, MarketOutcomeStatus } from "@finbackoffice/enums";
import { useGetCurrentFeedType } from "hooks";
import { FeedScopeEnum } from "utils/constants";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import styles from "./outcome.module.sass";

export type OutcomeVariant = "mainMarket" | "horizontal";

interface IProps {
    renderName?: (name: string | null) => ReactNode;
    outcome?: IMarketOutcomeFeedResponse | null;
    onOutcomeClick?: (outcome: IMarketOutcomeFeedResponse) => void;
    marketStatus?: MarketStatus;
    variant?: OutcomeVariant;
    active?: boolean;
}

const Outcome: FC<IProps> = ({
    outcome,
    onOutcomeClick,
    marketStatus,
    variant,
    active = false,
    renderName: renderNameProp,
}) => {
    const feed = useGetCurrentFeedType();

    const { siteCurrentOddFormat } = useContext(ConfigContextV2);

    const [arrowClass, setArrowClass] = useState<"increased" | "decreased" | "">("");

    const prevOdds: string = usePrevious(outcome?.odds) ?? "";
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isResultsFeed = feed === FeedScopeEnum.results;
    const isOutcomeDisabled =
        marketStatus === MarketStatus.OnHold ||
        (outcome && outcome.status === MarketOutcomeStatus.NotActive) ||
        !outcome;

    useEffect(() => {
        if (prevOdds && outcome) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (parseFloat(prevOdds) < parseFloat(outcome.odds)) {
                setArrowClass("increased");
            } else if (parseFloat(prevOdds) > parseFloat(outcome.odds)) {
                setArrowClass("decreased");
            }

            timeoutRef.current = setTimeout(() => {
                setArrowClass("");
            }, 10000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcome?.odds]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const onClickHandler = (event: SyntheticEvent) => {
        if (!isOutcomeDisabled) {
            event.stopPropagation();
            event.preventDefault();

            onOutcomeClick?.(outcome);
        }
    };

    const renderValue = useMemo(() => {
        if (!outcome) return;

        if (isResultsFeed && marketStatus) {
            const iconName = getBetStatus(marketStatus, outcome)?.toLowerCase().split("_").join("");
            const iconSrc = `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/status-${iconName}.svg`;

            return (
                <span className={styles.resultsOutcomeItemValue}>
                    <Svg src={iconSrc} className={classNames("svg-icon", iconName)} />
                </span>
            );
        }

        return (
            <span className={styles.outcomeItemValue}>
                {OddFormatter.format(parseFloat(outcome.odds), siteCurrentOddFormat)}
            </span>
        );
    }, [isResultsFeed, marketStatus, outcome, siteCurrentOddFormat]);

    const renderName = useMemo(() => {
        if (!outcome) return;
        if (renderNameProp) return renderNameProp(outcome.name);
        return <span>{outcome.name}</span>;
    }, [outcome, renderNameProp]);

    return (
        <div
            className={classNames(
                styles.outcomeItem,
                styles[arrowClass],
                variant && styles[variant],
                isOutcomeDisabled && styles.disabled,
                active && styles.active,
            )}
            data-testid={`outcome-${isOutcomeDisabled ? "disabled" : "enabled"}`}
            onClick={isResultsFeed ? undefined : onClickHandler}>
            {outcome ? (
                <>
                    {renderName}
                    {renderValue}
                </>
            ) : (
                "- -"
            )}
        </div>
    );
};
export default memo(Outcome);
